import {Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {MatTableDataSource} from "@angular/material/table";
import {ProjectTreeNode, ProjectTreeNodeCreateUpdate} from "./project-tree-node.interface";
import {ProjectFormData, ProjectType} from "./project-form-data.interface";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Subject, takeUntil} from "rxjs";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {ProjectNodeCreateUpdateComponent} from './project-node-create-update/project-node-create-update.component';
import {ProjectsService} from '@core/services/backend/common/service/projects.service';
import {ProjectTypesService} from '@core/services/backend/common/service/project-types.service';
import {mapBackendToFrontend, mapFrontendToBackend} from './project-mapper.util';
import {TableUtil} from "@vex/services/tableUtil";
import {SelectionModel} from '@angular/cdk/collections';
import {DialogComponent} from '@vex/components/dialog/dialog.component';
import {LevelsService} from '@core/services/backend/common/service/levels.service';
import {Level} from '@core/services/backend/common/interface/levels';

@Component({
    selector: 'vex-projectv2',
    templateUrl: './projectv2.component.html',
    styleUrls: ['./projectv2.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class Projectv2Component implements OnInit, OnDestroy {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    nodeList: ProjectTreeNode[] = [];
    flatNodeList: ProjectTreeNode[] = [];
    flatNodeListFiltered: ProjectTreeNode[] = []; // Lista completa filtrada para paginación del frontend
    dataSource: MatTableDataSource<ProjectTreeNode> | null;
    searchForm: FormGroup;
    selection = new SelectionModel<ProjectTreeNode>(true, []);
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<ProjectTreeNode>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.NOMBRE', property: 'name', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'GRIDS.CODIGO', property: 'code', type: 'text', visible: false}, // Oculto porque se muestra en columna personalizada
        {label: 'GRIDS.DESCRIPCION', property: 'description', type: 'text', visible: false},
        {label: 'GRIDS.NIVEL', property: 'level', type: 'text', visible: false},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    isLoading: boolean = false;

    // Propiedades de paginación
    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    totalRecords = 0;

    // Tipos de proyecto para el filtro
    projectTypes: ProjectType[] = [];

    constructor(
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar,
        private projectsService: ProjectsService,
        private projectTypesService: ProjectTypesService,
        private levelsService: LevelsService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            search: new FormControl(''),
            tipo: new FormControl('') // Filtro por tipo de proyecto
        });
        
        // Cargar tipos de proyecto desde el backend
        this.loadProjectTypes();
        
        this.loadData();
    }

    /**
     * Carga los tipos de proyecto desde el backend
     */
    /**
     * Solo tipos Proyecto y ODS exportan PEP; administrativo y cotización no.
     */
    private tipoUsesPep(tipoId: string | undefined): boolean {
        if (!tipoId) return false;
        const t = this.projectTypes.find(x => x.id === tipoId);
        const raw = (t?.name || '').toLowerCase();
        const name = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (name.includes('administrativ')) return false;
        if (name.includes('cotiz')) return false;
        return name.includes('proyecto') || name.includes('ods');
    }

    loadProjectTypes(): void {
        this.projectTypesService.list('', 0, 1000, 'name ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                const types = response?.data || response?.Data || (Array.isArray(response) ? response : []);
                // Agregar opción "Todos los tipos" al inicio
                this.projectTypes = [
                    { id: '', name: 'Todos los tipos' },
                    ...types
                        .filter((item: any) => item && item.active !== false)
                        .map((item: any) => ({
                            id: item.id?.toString() || item.ID?.toString() || '',
                            name: item.name || ''
                        }))
                ];
            },
            error: (err) => {
                console.error('Error loading project types:', err);
                // En caso de error, usar opción por defecto
                this.projectTypes = [
                    { id: '', name: 'Todos los tipos' }
                ];
            }
        });
    }

    loadData() {
        this.isLoading = true;
        
        // Construir el objeto de búsqueda
        const searchObj = this.getSearch();
        const searchString = Object.keys(searchObj).length > 0 ? JSON.stringify(searchObj) : '';
        const tipoFiltro = this.searchForm.get('tipo')?.value;
        const searchValue = this.searchForm.get('search')?.value;
        const hasSearch = searchValue && searchValue.trim() !== '';
        
        // Si hay filtro de tipo o búsqueda, cargar todos los datos (sin paginación del backend)
        // porque el filtro se aplica en el frontend y el backend no calcula correctamente el total con búsqueda
        const needsFrontendPagination = (tipoFiltro && tipoFiltro !== '') || hasSearch;
        const pageSizeForBackend = needsFrontendPagination ? 10000 : this.pageSize;
        const pageForBackend = needsFrontendPagination ? 0 : this.currentPage;
        
        // Si hay búsqueda, NO filtrar por parent_id para poder buscar en todos los niveles
        // Si NO hay búsqueda, filtrar solo proyectos raíz (sin parent_id)
        const searchObjWithParentId = hasSearch ? searchObj : { ...searchObj, parentID: null };
        const searchStringWithParentId = Object.keys(searchObjWithParentId).length > 0 ? JSON.stringify(searchObjWithParentId) : (hasSearch ? '' : JSON.stringify({ parentID: null }));
        
        console.log('Búsqueda enviada:', searchStringWithParentId);
        console.log('Página actual:', this.currentPage);
        console.log('Tamaño de página:', this.pageSize);
        console.log('Filtro de tipo:', tipoFiltro);
        console.log('Paginación frontend:', needsFrontendPagination);
        console.log('Hay búsqueda:', hasSearch);
        console.log('Cargando proyectos:', hasSearch ? 'todos los niveles' : 'solo nivel 0 (raíz)');
        
        // Cargar datos del backend
        this.projectsService.list(searchStringWithParentId, pageForBackend, pageSizeForBackend, 'id ASC', false).subscribe({
            next: (response: any) => {
                // Debug: ver qué devuelve el backend
                console.log('Respuesta del backend:', response);
                console.log('Tipo de respuesta:', typeof response);
                console.log('Es array?', Array.isArray(response));
                
                // El backend puede devolver diferentes formatos:
                // 1. { Data: [...], TotalRecords: number } (PagedResults)
                // 2. Array directo [...]
                // 3. Objeto con otras propiedades
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    // Si es un array directo
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    // Si tiene la propiedad Data que es un array
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    // Si tiene la propiedad data (minúscula) que es un array
                    projects = response.data;
                } else {
                    // Si no es ninguno de los formatos esperados, usar array vacío
                    console.warn('Formato de respuesta inesperado del backend:', response);
                    projects = [];
                }
                
                console.log('Proyectos extraídos:', projects);
                console.log('Cantidad de proyectos:', projects.length);
                
                // Guardar la respuesta del backend para usarla después
                const backendResponse = response;
                
                // Si hay búsqueda, procesar todos los proyectos y agrupar por padre
                if (hasSearch) {
                    this.processSearchResults(projects, backendResponse);
                } else {
                    // Si NO hay búsqueda, filtrar solo proyectos raíz (sin parent_id) en el frontend
                    const rootProjects = projects.filter((project: any) => {
                        const frontendData = mapBackendToFrontend(project);
                        const parentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
                        // Un proyecto es raíz si no tiene parent_id (null, undefined, 0, o vacío)
                        return !parentId || parentId === 'null' || parentId === 'undefined' || parentId === '0';
                    });
                    
                    console.log('Proyectos de nivel 0 (raíz) filtrados:', rootProjects.length);
                    
                    // Verificar cuáles nodos tienen hijos antes de construir el árbol
                    this.verifyNodesWithChildren(rootProjects).then(() => {
                        // Convertir datos del backend a estructura de árbol (solo nivel 0)
                        // Los subniveles se cargarán cuando se expanda un nodo
                        this.nodeList = this.buildTreeFromBackendData(rootProjects);
                        
                        // Continuar con el resto del procesamiento
                        this.processNodeList(backendResponse);
                    });
                }
            },
            error: (error) => {
                console.error('Error al cargar proyectos:', error);
                this.handleErrorResponse('Error al cargar los proyectos');
                // En caso de error, usar datos mock como fallback
        this.initializeMockData();
        this.updateFlatList();
        this.isLoading = false;
            }
        });
    }

    /**
     * Procesa los resultados de búsqueda y agrupa por padre
     * Si un hijo coincide con la búsqueda, incluye su padre y todos sus hermanos
     */
    private processSearchResults(allProjects: any[], backendResponse: any): void {
        const searchValue = this.searchForm.get('search')?.value?.trim().toUpperCase() || '';
        
        // Crear un mapa de todos los proyectos por ID
        const projectsMap = new Map<string, any>();
        allProjects.forEach((project: any) => {
            const id = project.id?.toString() || project.ID?.toString() || mapBackendToFrontend(project).id || '';
            if (id) {
                projectsMap.set(id, project);
            }
        });
        
        // Encontrar todos los proyectos que coinciden con la búsqueda
        const matchingProjects: any[] = [];
        const parentIdsToInclude = new Set<string>();
        
        allProjects.forEach((project: any) => {
            const frontendData = mapBackendToFrontend(project);
            const name = (frontendData.proyecto || project.name || '').toUpperCase();
            const code = (frontendData.codigo || project.code || '').toUpperCase();
            const parentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
            const projectId = frontendData.id || project.id?.toString() || project.ID?.toString() || '';
            
            // Verificar si coincide con la búsqueda
            if (name.includes(searchValue) || code.includes(searchValue)) {
                matchingProjects.push(project);
                
                // Si es un hijo (tiene parent_id), agregar su padre a la lista de padres a incluir
                if (parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0') {
                    parentIdsToInclude.add(parentId);
                }
                
                // Si es un padre (sin parent_id), agregar su ID a la lista de padres a incluir
                // para que se incluyan TODOS sus hijos
                if ((!parentId || parentId === 'null' || parentId === 'undefined' || parentId === '0') && projectId) {
                    parentIdsToInclude.add(projectId);
                }
            }
        });
        
        console.log('Proyectos que coinciden con la búsqueda:', matchingProjects.length);
        console.log('Padres a incluir:', Array.from(parentIdsToInclude));
        
        // Cargar todos los proyectos necesarios (coincidencias + sus padres + hermanos)
        const projectsToLoad = new Set<string>();
        
        // Agregar todos los proyectos que coinciden
        matchingProjects.forEach((project: any) => {
            const id = project.id?.toString() || project.ID?.toString() || mapBackendToFrontend(project).id || '';
            if (id) {
                projectsToLoad.add(id);
            }
        });
        
        // Agregar todos los padres
        parentIdsToInclude.forEach((parentId) => {
            projectsToLoad.add(parentId);
        });
        
        // Agregar todos los hermanos (hijos de los padres incluidos)
        // IMPORTANTE: Esto incluye TODOS los hijos de los padres, incluso si no coinciden con la búsqueda
        parentIdsToInclude.forEach((parentId) => {
            allProjects.forEach((project: any) => {
                const projectParentId = project.parent_id?.toString() || 
                                       project.ParentID?.toString() || 
                                       mapBackendToFrontend(project).parentId || '';
                // Normalizar parent_id para comparación
                const normalizedParentId = projectParentId && projectParentId !== 'null' && projectParentId !== 'undefined' && projectParentId !== '0' ? projectParentId.trim() : '';
                const normalizedParentIdToInclude = parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0' ? parentId.trim() : '';
                
                if (normalizedParentId === normalizedParentIdToInclude && normalizedParentId) {
                    const id = project.id?.toString() || project.ID?.toString() || mapBackendToFrontend(project).id || '';
                    if (id) {
                        projectsToLoad.add(id);
                    }
                }
            });
        });
        
        console.log('Proyectos a cargar después de agregar hermanos:', Array.from(projectsToLoad).length);
        console.log('IDs de proyectos a cargar:', Array.from(projectsToLoad).slice(0, 10));
        
        // Obtener todos los proyectos a cargar
        const projectsToProcess = Array.from(projectsToLoad)
            .map(id => projectsMap.get(id))
            .filter(p => p !== undefined);
        
        console.log('Proyectos a procesar (coincidencias + padres + hermanos):', projectsToProcess.length);
        
        // Separar en padres (sin parent_id) e hijos (con parent_id)
        // IMPORTANTE: Cuando hay búsqueda, los proyectos que coinciden deben mostrarse como raíz
        // incluso si tienen un parent_id, para que se muestren con sus hijos debajo
        const matchingProjectIds = new Set(matchingProjects.map((p: any) => {
            const frontendData = mapBackendToFrontend(p);
            return frontendData.id || p.id?.toString() || p.ID?.toString() || '';
        }).filter(id => id));
        
        // Crear un mapa de todos los IDs de proyectos para verificar si un padre existe
        const allProjectIds = new Set(projectsToProcess.map((p: any) => {
            const frontendData = mapBackendToFrontend(p);
            return frontendData.id || p.id?.toString() || p.ID?.toString() || '';
        }).filter(id => id));
        
        const rootProjects = projectsToProcess.filter((project: any) => {
            const frontendData = mapBackendToFrontend(project);
            const projectId = frontendData.id || project.id?.toString() || project.ID?.toString() || '';
            const parentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
            
            // Normalizar parent_id
            const normalizedParentId = parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0' ? parentId.trim() : '';
            
            // Un proyecto es raíz si:
            // 1. No tiene parent_id (null, undefined, 0, o vacío), O
            // 2. Coincide con la búsqueda Y su padre NO está en los resultados (o no tiene padre)
            const isRootByParent = !normalizedParentId;
            const isMatching = matchingProjectIds.has(projectId);
            const parentExistsInResults = normalizedParentId && allProjectIds.has(normalizedParentId);
            
            // Si coincide con la búsqueda pero su padre también está en los resultados, debe ser hijo, no raíz
            if (isMatching && parentExistsInResults) {
                return false; // Será hijo de su padre
            }
            
            return isRootByParent || isMatching;
        });
        
        const childProjects = projectsToProcess.filter((project: any) => {
            const frontendData = mapBackendToFrontend(project);
            const projectId = frontendData.id || project.id?.toString() || project.ID?.toString() || '';
            const parentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
            
            // Normalizar parent_id
            const normalizedParentId = parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0' ? parentId.trim() : '';
            
            // Un proyecto es hijo si:
            // 1. Tiene parent_id válido, Y
            // 2. Su padre está en los resultados (ya sea como raíz o como coincidencia)
            const hasParentId = !!normalizedParentId;
            const parentExistsInResults = normalizedParentId && allProjectIds.has(normalizedParentId);
            
            return hasParentId && parentExistsInResults;
        });
        
        // Ordenar los proyectos raíz por ID para mantener consistencia
        rootProjects.sort((a: any, b: any) => {
            const idA = parseInt(a.id?.toString() || a.ID?.toString() || mapBackendToFrontend(a).id || '0', 10);
            const idB = parseInt(b.id?.toString() || b.ID?.toString() || mapBackendToFrontend(b).id || '0', 10);
            return idA - idB;
        });
        
        // Ordenar los hijos por parent_id primero, luego por ID para agruparlos correctamente
        childProjects.sort((a: any, b: any) => {
            const frontendDataA = mapBackendToFrontend(a);
            const frontendDataB = mapBackendToFrontend(b);
            const parentIdA = frontendDataA.parentId || a.parent_id?.toString() || a.ParentID?.toString() || '';
            const parentIdB = frontendDataB.parentId || b.parent_id?.toString() || b.ParentID?.toString() || '';
            
            // Primero ordenar por parent_id
            if (parentIdA !== parentIdB) {
                return parentIdA.localeCompare(parentIdB);
            }
            
            // Si tienen el mismo parent_id, ordenar por ID
            const idA = parseInt(frontendDataA.id || a.id?.toString() || a.ID?.toString() || '0', 10);
            const idB = parseInt(frontendDataB.id || b.id?.toString() || b.ID?.toString() || '0', 10);
            return idA - idB;
        });
        
        console.log('Proyectos raíz a mostrar:', rootProjects.length);
        console.log('Proyectos hijos a mostrar:', childProjects.length);
        
        // SIMPLIFICACIÓN: Construir el árbol completo basándose solo en parent_id (como funcionaba con level)
        // Verificar cuáles nodos tienen hijos usando TODOS los proyectos disponibles
        this.verifyNodesWithChildren(projectsToProcess, allProjects).then(() => {
            // Construir el árbol completo con todos los proyectos a mostrar
            // buildTreeFromBackendData construye el árbol basándose en parent_id
            this.nodeList = this.buildTreeFromBackendData(projectsToProcess);
            
            // Ordenar los nodos raíz por ID para mantener consistencia
            this.nodeList.sort((a, b) => {
                const idA = parseInt(a.id || '0', 10);
                const idB = parseInt(b.id || '0', 10);
                return idA - idB;
            });
            
            // Expandir automáticamente los nodos que coinciden con la búsqueda
            const expandMatchingNodes = (nodes: ProjectTreeNode[]): void => {
                nodes.forEach(node => {
                    if (matchingProjectIds.has(node.id)) {
                        node.expanded = true;
                    }
                    // Si el nodo tiene hijos que coinciden, también expandirlo
                    if (node.children && node.children.length > 0) {
                        const hasMatchingChild = node.children.some(child => matchingProjectIds.has(child.id));
                        if (hasMatchingChild) {
                            node.expanded = true;
                        }
                        expandMatchingNodes(node.children);
                    }
                });
            };
            expandMatchingNodes(this.nodeList);
            
            // Continuar con el resto del procesamiento
            this.processNodeList(backendResponse);
        });
    }

    /**
     * Carga los hijos en los nodos del árbol para los resultados de búsqueda
     * Retorna una Promise que se resuelve cuando todos los hijos han sido cargados
     */
    private loadChildrenForSearchResults(nodes: ProjectTreeNode[], childProjects: any[], allProjects?: any[]): Promise<void> {
        const promises: Promise<void>[] = [];
        
        // Usar allProjects si está disponible (debe ser projectsToProcess), de lo contrario usar childProjects
        // allProjects aquí contiene todos los proyectos a mostrar, no solo los que coinciden
        const projectsToSearch = allProjects && allProjects.length > 0 ? allProjects : childProjects;
        
        console.log('🔍 Buscando hijos en:', projectsToSearch.length, 'proyectos');
        
        nodes.forEach((node) => {
            // Buscar hijos de este nodo en todos los proyectos disponibles
            // Un proyecto es hijo si su parent_id coincide con el ID del nodo
            const children = projectsToSearch.filter((project: any) => {
                const frontendData = mapBackendToFrontend(project);
                const projectId = frontendData.id || project.id?.toString() || project.ID?.toString() || '';
                const projectParentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
                
                // Normalizar parent_id para comparación
                const normalizedParentId = projectParentId && projectParentId !== 'null' && projectParentId !== 'undefined' && projectParentId !== '0' ? projectParentId.trim() : '';
                const normalizedNodeId = node.id ? node.id.trim() : '';
                
                // Un proyecto es hijo si su parent_id coincide con el ID del nodo
                // Y el proyecto no es el mismo nodo (evitar bucles)
                return normalizedParentId === normalizedNodeId && normalizedParentId && projectId !== normalizedNodeId;
            });
            
            if (children.length > 0) {
                console.log(`✅ Nodo ${node.id} (${node.name}): encontró ${children.length} hijos`);
            }
            
            // Ordenar los hijos por ID para mantener un orden consistente
            children.sort((a: any, b: any) => {
                const idA = parseInt(a.id?.toString() || a.ID?.toString() || mapBackendToFrontend(a).id || '0', 10);
                const idB = parseInt(b.id?.toString() || b.ID?.toString() || mapBackendToFrontend(b).id || '0', 10);
                return idA - idB;
            });
            
            if (children.length > 0) {
                // Verificar cuáles de estos hijos tienen hijos propios usando todos los proyectos
                const promise = this.verifyNodesWithChildren(children, allProjects).then(() => {
                    // Mapear los hijos a ProjectTreeNode
                    const childrenNodes = children.map((project: any) => {
                        const frontendData = mapBackendToFrontend(project);
                        const hasChildrenValue = (project as any).hasChildren;
                        return {
                            id: frontendData.id || project.id?.toString() || '',
                            name: frontendData.proyecto || project.name || '',
                            code: frontendData.codigo || project.code || '',
                            description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                            level: frontendData.level || project.level || 0,
                            parentId: frontendData.parentId || (project.parent_id ? project.parent_id.toString() : undefined),
                            expanded: true, // Expandir automáticamente los hijos en resultados de búsqueda
                            hasChildren: Boolean(hasChildrenValue),
                            children: [],
                            createdAt: project.created_at ? new Date(project.created_at) : new Date(),
                            updatedAt: project.updated_at ? new Date(project.updated_at) : undefined,
                            active: frontendData.activo !== false,
                            visible: frontendData.visible !== false,
                            aprobado: frontendData.aprobado || false,
                            requiereObservacion: frontendData.requiereObservacion || false,
                            horasExtras: frontendData.horasExtras !== false,
                            horasNormales: frontendData.horasNormales !== false,
                            moneda: frontendData.moneda,
                            pep: frontendData.pep,
                            fechaInicio: frontendData.fechaInicio,
                            fechaTermino: frontendData.fechaTermino,
                            fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                            id_jefeProyecto: frontendData.id_jefeProyecto,
                            id_controlProyecto: frontendData.id_controlProyecto,
                            id_controlDocumental: frontendData.id_controlDocumental,
                            HHEstimadas: frontendData.HHEstimadas,
                            tipo: frontendData.tipo || project.tipo || project.Tipo || '',
                            id_cliente: frontendData.id_cliente || project.id_cliente || project.IdCliente || '',
                            origen: frontendData.origen || project.origen || project.Origen || '',
                            codigoPadre: frontendData.codigoPadre || project.codigo_padre || project.CodigoPadre || undefined,
                            codigoGlosa: frontendData.codigoGlosa || project.codigo_glosa || project.CodigoGlosa || undefined,
                            codigoLista: frontendData.codigoLista || project.codigo_lista || project.CodigoLista || undefined
                        } as ProjectTreeNode;
                    });
                    
                    // Asignar los hijos al nodo
                    node.children = childrenNodes;
                    node.hasChildren = childrenNodes.length > 0;
                    node.expanded = true; // Expandir automáticamente para mostrar los resultados
                    
                    // Recursivamente cargar hijos de hijos si los hay
                    // Usar allProjects si está disponible para buscar hijos anidados
                    return this.loadChildrenForSearchResults(childrenNodes, childProjects, allProjects);
                });
                
                promises.push(promise);
            }
        });
        
        // Retornar una Promise que se resuelve cuando todos los hijos han sido cargados
        return Promise.all(promises).then(() => {
            console.log('Todos los hijos han sido cargados y asignados a sus padres');
        });
    }

    /**
     * Verifica cuáles nodos tienen hijos haciendo una consulta al backend
     * @param projects Los proyectos a verificar
     * @param allProjectsForVerification Opcional: todos los proyectos disponibles para verificación (evita consulta adicional)
     */
    private verifyNodesWithChildren(projects: any[], allProjectsForVerification?: any[]): Promise<void> {
        return new Promise((resolve) => {
            // Obtener todos los IDs de los proyectos de nivel 0
            const nodeIds = projects.map(p => {
                const id = p.id?.toString() || p.ID?.toString() || mapBackendToFrontend(p).id || '';
                return id;
            }).filter(id => id !== '');

            if (nodeIds.length === 0) {
                resolve();
                return;
            }

            // Si se proporciona allProjectsForVerification, usarlo directamente
            // Esto evita una consulta adicional al backend cuando ya tenemos todos los proyectos
            if (allProjectsForVerification && allProjectsForVerification.length > 0) {
                this.processHasChildrenVerification(projects, allProjectsForVerification);
                resolve();
                return;
            }

            // Consultar todos los proyectos para obtener los parent_id
            this.projectsService.list('', 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
                next: (response: any) => {
                    let allProjects: any[] = [];
                    
                    if (Array.isArray(response)) {
                        allProjects = response;
                    } else if (response?.Data && Array.isArray(response.Data)) {
                        allProjects = response.Data;
                    } else if (response?.data && Array.isArray(response.data)) {
                        allProjects = response.data;
                    }

                    this.processHasChildrenVerification(projects, allProjects);
                    resolve();
                },
                error: (err) => {
                    console.error('Error al verificar nodos con hijos:', err);
                    // En caso de error, asumir que no tienen hijos
                    projects.forEach((project: any) => {
                        (project as any).hasChildren = false;
                    });
                    resolve();
                }
            });
        });
    }

    /**
     * Procesa la verificación de hasChildren usando todos los proyectos disponibles
     */
    private processHasChildrenVerification(projects: any[], allProjects: any[]): void {
        // Crear un Set con los parent_id que existen
        // Normalizar todos los IDs a string para comparación consistente
        const parentIds = new Set<string>();
        allProjects.forEach((project: any) => {
            // Intentar obtener parent_id de múltiples formatos posibles
            const parentId = project.parent_id?.toString() || 
                           project.ParentID?.toString() || 
                           project.parentId?.toString() || 
                           project.ParentId?.toString() ||
                           (project.parent_id !== null && project.parent_id !== undefined ? String(project.parent_id) : null) ||
                           (project.ParentID !== null && project.ParentID !== undefined ? String(project.ParentID) : null);
            if (parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0') {
                // Normalizar el ID eliminando espacios y asegurando que sea string
                const normalizedParentId = String(parentId).trim();
                if (normalizedParentId) {
                    parentIds.add(normalizedParentId);
                }
            }
        });

        console.log('Parent IDs encontrados:', Array.from(parentIds).slice(0, 10), '... (total:', parentIds.size, ')');

        // Marcar en los proyectos cuáles tienen hijos
        projects.forEach((project: any) => {
            // Obtener el ID del proyecto de múltiples formatos posibles
            const projectIdRaw = project.id || project.ID || mapBackendToFrontend(project).id;
            // Normalizar el ID a string
            const projectId = projectIdRaw !== null && projectIdRaw !== undefined ? String(projectIdRaw).trim() : '';
            
            if (!projectId) {
                console.warn('Proyecto sin ID válido:', project);
                (project as any).hasChildren = false;
                return;
            }
            
            const hasChildren = parentIds.has(projectId);
            (project as any).hasChildren = hasChildren;
            
            // Log detallado para debugging
            if (projectId === '570' || projectId === '31') {
                console.log(`🔍 DEBUG Proyecto ${projectId} (${project.name || mapBackendToFrontend(project).proyecto}):`, {
                    projectId,
                    projectIdType: typeof projectId,
                    hasChildren,
                    parentIdsHas: parentIds.has(projectId),
                    parentIdsSample: Array.from(parentIds).slice(0, 5),
                    rawId: project.id,
                    rawID: project.ID,
                    mappedId: mapBackendToFrontend(project).id
                });
            } else {
                console.log(`Proyecto ${projectId} (${project.name || mapBackendToFrontend(project).proyecto}): hasChildren = ${hasChildren}`);
            }
        });
        
        console.log('Total de proyectos con hijos:', projects.filter((p: any) => (p as any).hasChildren).length);
    }

    /**
     * Procesa la lista de nodos después de verificar los hijos
     */
    private processNodeList(backendResponse?: any): void {
        const tipoFiltro = this.searchForm.get('tipo')?.value;
        
        // Aplicar filtro por tipo si está seleccionado (después de construir el árbol)
        if (tipoFiltro && tipoFiltro !== '') {
            this.nodeList = this.filterTreeByType(this.nodeList, tipoFiltro);
        }
        
        // Actualizar la lista plana completa (esto incluye todos los nodos del árbol, expandidos o no)
        this.updateFlatList();
        
        // Forzar detección de cambios después de actualizar la lista
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 0);
        
        // Continuar con el resto del procesamiento
        this.finishLoadData(backendResponse);
    }

    /**
     * Finaliza el proceso de carga de datos
     */
    private finishLoadData(backendResponse?: any): void {
        const tipoFiltro = this.searchForm.get('tipo')?.value;
        const searchValue = this.searchForm.get('search')?.value;
        const hasSearch = searchValue && searchValue.trim() !== '';
        const needsFrontendPagination = (tipoFiltro && tipoFiltro !== '') || hasSearch;
        
        // Guardar la lista completa filtrada
        this.flatNodeListFiltered = [...this.flatNodeList];
        
        if (needsFrontendPagination) {
            // Si hay filtro de tipo o búsqueda, el total es la cantidad de nodos filtrados
            this.totalRecords = this.flatNodeListFiltered.length;
            
            // Aplicar paginación del frontend
            const startIndex = this.currentPage * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            this.flatNodeList = this.flatNodeListFiltered.slice(startIndex, endIndex);
            
            // Actualizar el dataSource con la lista paginada
            this.dataSource.data = this.flatNodeList;
        } else {
            // Si no hay filtros, usar el totalRecords del backend
            // El backend ya aplicó la paginación, así que solo mostramos los registros recibidos
            if (backendResponse?.TotalRecords !== undefined) {
                this.totalRecords = backendResponse.TotalRecords;
            } else if (backendResponse?.totalRecords !== undefined) {
                this.totalRecords = backendResponse.totalRecords;
            } else if (backendResponse?.total_records !== undefined) {
                this.totalRecords = backendResponse.total_records;
            } else {
                // Si no se puede determinar, usar la cantidad de nodos en la lista plana
                this.totalRecords = this.flatNodeList.length;
            }
        }
        
        console.log('Total de registros calculado:', this.totalRecords);
        console.log('Registros en lista plana:', this.flatNodeList.length);
        
        // Actualizar el paginator después de actualizar la lista plana
        setTimeout(() => {
            if (this.paginator) {
                this.paginator.length = this.totalRecords;
                this.paginator.pageIndex = this.currentPage;
                this.paginator.pageSize = this.pageSize;
            }
        }, 0);
        
        this.isLoading = false;
    }

    /**
     * Filtra los nodos del árbol por tipo de proyecto
     */
    private filterTreeByType(nodes: ProjectTreeNode[], tipoFiltro: string): ProjectTreeNode[] {
        if (!tipoFiltro || tipoFiltro === '') {
            return nodes;
        }

        return nodes.filter(node => {
            // Verificar si el nodo coincide con el tipo
            const nodeTipo = this.getNodeType(node);
            const matches = nodeTipo === tipoFiltro;

            // Si tiene hijos, filtrarlos también
            if (node.children && node.children.length > 0) {
                node.children = this.filterTreeByType(node.children, tipoFiltro);
                // Si el nodo no coincide pero tiene hijos que sí, mantenerlo
                if (!matches && node.children.length > 0) {
                    return true;
                }
            }

            return matches;
        });
    }

    /**
     * Obtiene el tipo de un nodo
     */
    private getNodeType(node: ProjectTreeNode): string {
        // El tipo está almacenado como propiedad adicional en el nodo
        return (node as any).tipo || '';
    }

    /**
     * Construye la estructura de árbol desde los datos planos del backend
     */
    private buildTreeFromBackendData(projects: any[]): ProjectTreeNode[] {
        // Validar que projects sea un array
        if (!Array.isArray(projects)) {
            console.error('buildTreeFromBackendData: projects no es un array', projects);
            return [];
        }

        // Si no hay proyectos, retornar array vacío
        if (projects.length === 0) {
            return [];
        }

        // Crear un mapa de nodos por ID
        const nodeMap = new Map<string, ProjectTreeNode>();
        const rootNodes: ProjectTreeNode[] = [];

        // Primero, crear todos los nodos
        projects.forEach((project: any) => {
            const frontendData = mapBackendToFrontend(project);
            
            // Normalizar parent_id para asegurar consistencia
            const rawParentId = frontendData.parentId || project.parent_id?.toString() || project.ParentID?.toString() || '';
            const normalizedParentId = rawParentId && rawParentId !== 'null' && rawParentId !== 'undefined' && rawParentId !== '0' ? rawParentId.trim() : undefined;
            
            const projectId = frontendData.id || project.id?.toString() || '';
            
            // Debug para proyectos específicos
            if (projectId === '32' || projectId === '31') {
                console.log(`📦 Creando nodo ${projectId}: rawParentId="${rawParentId}", normalizedParentId="${normalizedParentId}"`);
                console.log(`   project.parent_id:`, project.parent_id, `tipo:`, typeof project.parent_id);
                console.log(`   frontendData.parentId:`, frontendData.parentId, `tipo:`, typeof frontendData.parentId);
            }
            
            const node: ProjectTreeNode = {
                id: projectId,
                name: frontendData.proyecto || project.name || '',
                code: frontendData.codigo || project.code || '',
                description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                level: frontendData.level || project.level || 0,
                parentId: normalizedParentId,
                expanded: false,
                hasChildren: false,
                children: [],
                createdAt: project.created_at ? new Date(project.created_at) : new Date(),
                updatedAt: project.updated_at ? new Date(project.updated_at) : undefined,
                active: frontendData.activo !== false,
                visible: frontendData.visible !== false,
                aprobado: frontendData.aprobado || false,
                requiereObservacion: frontendData.requiereObservacion || false,
                horasExtras: frontendData.horasExtras !== false,
                horasNormales: frontendData.horasNormales !== false,
                moneda: frontendData.moneda,
                pep: frontendData.pep,
                fechaInicio: frontendData.fechaInicio,
                fechaTermino: frontendData.fechaTermino,
                fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                id_jefeProyecto: frontendData.id_jefeProyecto,
                id_controlProyecto: frontendData.id_controlProyecto,
                id_controlDocumental: frontendData.id_controlDocumental,
                HHEstimadas: frontendData.HHEstimadas
            };
            
            // Agregar campos adicionales al nodo para facilitar el acceso
            (node as any).tipo = frontendData.tipo || project.tipo || project.Tipo || '';
            (node as any).id_cliente = frontendData.id_cliente || project.id_cliente || project.IdCliente || '';
            (node as any).origen = frontendData.origen || project.origen || project.Origen || '';
            // Agregar campos separados del código para tipo proyecto
            node.codigoPadre = frontendData.codigoPadre || project.codigo_padre || project.CodigoPadre || undefined;
            node.codigoGlosa = frontendData.codigoGlosa || project.codigo_glosa || project.CodigoGlosa || undefined;
            node.codigoLista = frontendData.codigoLista || project.codigo_lista || project.CodigoLista || undefined;
            
            // Usar el hasChildren que se verificó previamente en verifyNodesWithChildren
            // Si no se verificó (por error o porque no se ejecutó), usar false
            const hasChildrenValue = (project as any).hasChildren;
            // Asegurarse de que hasChildren sea un booleano
            node.hasChildren = Boolean(hasChildrenValue);
            
            console.log(`Nodo ${node.id} (${node.name}): hasChildren = ${node.hasChildren} (valor del proyecto: ${hasChildrenValue}, tipo: ${typeof hasChildrenValue})`);
            
            nodeMap.set(node.id, node);
        });

        // Construir la jerarquía basándose en parent_id
        nodeMap.forEach((node) => {
            // Normalizar parent_id para comparación
            const parentId = node.parentId ? node.parentId.toString().trim() : null;
            
            // Debug para nodos específicos
            if (node.id === '32' || node.id === '31') {
                console.log(`🔍 Construyendo árbol - Nodo ${node.id} (${node.name}): parentId="${parentId}", tipo=${typeof node.parentId}`);
            }
            
            // Si tiene parent_id válido, buscar el nodo padre y agregarlo como hijo
            if (parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '0' && parentId !== '') {
                // Buscar el padre en el mapa (normalizar el ID también)
                const parentNode = nodeMap.get(parentId);
                if (parentNode) {
                    // El nodo es hijo del padre
                    if (!parentNode.children) {
                        parentNode.children = [];
                    }
                    parentNode.children.push(node);
                    parentNode.hasChildren = true;
                    
                    // Debug para nodos específicos
                    if (node.id === '32' || node.id === '31') {
                        console.log(`✅ Nodo ${node.id} (${node.name}) vinculado como hijo de ${parentId} (${parentNode.name})`);
                    }
                } else {
                    // Si el padre no está en el mapa, es un nodo raíz
                    // Esto puede pasar si el padre no está en los proyectos cargados
                    console.warn(`⚠️ Nodo ${node.id} (${node.name}) tiene padre ${parentId} que no está en el mapa, tratando como raíz`);
                    console.warn(`   IDs disponibles en el mapa:`, Array.from(nodeMap.keys()).slice(0, 10));
                    rootNodes.push(node);
                }
            } else {
                // Si no tiene parent_id válido, es un nodo raíz
                if (node.id === '32' || node.id === '31') {
                    console.log(`🌳 Nodo ${node.id} (${node.name}) es raíz (sin parent_id válido)`);
                }
                rootNodes.push(node);
            }
        });

        // Ordenar los hijos de cada nodo por ID
        const sortChildren = (nodes: ProjectTreeNode[]): void => {
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    node.children.sort((a, b) => {
                        const idA = parseInt(a.id || '0', 10);
                        const idB = parseInt(b.id || '0', 10);
                        return idA - idB;
                    });
                    sortChildren(node.children);
                }
            });
        };
        sortChildren(rootNodes);

        return rootNodes;
    }

    /**
     * Maneja errores de la API
     */
    private handleErrorResponse(message: string): void {
        this.snackbar.open(message, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
        });
    }

    private initializeMockData() {
        // Datos de ejemplo para demostrar la funcionalidad
        this.nodeList = [
            {
                id: '1',
                name: 'Proyecto Principal A',
                code: 'PA001',
                description: 'Proyecto de desarrollo de infraestructura',
                level: 0,
                expanded: false,
                hasChildren: true,
                children: [
                    {
                        id: '1-1',
                        name: 'Módulo Backend',
                        code: 'PA001-BE',
                        description: 'Desarrollo del backend del proyecto',
                        level: 1,
                        parentId: '1',
                        expanded: false,
                        hasChildren: true,
                        children: [
                            {
                                id: '1-1-1',
                                name: 'API REST',
                                code: 'PA001-BE-API',
                                description: 'Desarrollo de API REST',
                                level: 2,
                                parentId: '1-1',
                                expanded: false,
                                hasChildren: false,
                                children: []
                            }
                        ]
                    },
                    {
                        id: '1-2',
                        name: 'Módulo Frontend',
                        code: 'PA001-FE',
                        description: 'Desarrollo del frontend del proyecto',
                        level: 1,
                        parentId: '1',
                        expanded: false,
                        hasChildren: false,
                        children: []
                    }
                ]
            },
            {
                id: '2',
                name: 'Proyecto Principal B',
                code: 'PB001',
                description: 'Proyecto de mejora de procesos',
                level: 0,
                expanded: false,
                hasChildren: false,
                children: []
            }
        ];
    }

    updateFlatList() {
        this.flatNodeList = [];
        this.flattenTree(this.nodeList);
        // Crear una nueva referencia del array para forzar la detección de cambios
        this.dataSource.data = [...this.flatNodeList];
        console.log('Lista plana actualizada:', this.flatNodeList.length, 'nodos');
        console.log('Nodos expandidos:', this.flatNodeList.filter(n => n.expanded).length);
        console.log('Nodos con hasChildren=true:', this.flatNodeList.filter(n => n.hasChildren).map(n => `${n.id} (${n.name})`));
        console.log('Verificando nodos en dataSource:', this.dataSource.data.filter(n => n.hasChildren).map(n => `${n.id} (${n.name}): hasChildren=${n.hasChildren}, tipo=${typeof n.hasChildren}`));
        
        // Verificar cada nodo individualmente
        this.dataSource.data.forEach((node, index) => {
            if (node.id === '31') {
                console.log(`Nodo 31 en dataSource[${index}]:`, {
                    id: node.id,
                    name: node.name,
                    hasChildren: node.hasChildren,
                    hasChildrenType: typeof node.hasChildren,
                    children: node.children,
                    childrenLength: node.children?.length
                });
            }
        });
        
        // Forzar detección de cambios para asegurar que la vista se actualice
        this.cdr.detectChanges();
    }

    /**
     * Verifica si un nodo tiene hijos (para usar en el template)
     */
    hasChildren(node: ProjectTreeNode): boolean {
        return node.hasChildren === true || (node.children && node.children.length > 0);
    }

    private flattenTree(nodes: ProjectTreeNode[]) {
        for (const node of nodes) {
            this.flatNodeList.push(node);
            // Solo incluir hijos si el nodo está expandido
            // Los subniveles se cargarán cuando se expanda el nodo
            if (node.expanded && node.children && node.children.length > 0) {
                this.flattenTree(node.children);
            }
        }
    }

    toggleNode(node: ProjectTreeNode) {
        if (!node.expanded) {
            if (!node.children || node.children.length === 0) {
                this.loadChildrenForNode(node, () => {
                    if (node.children && node.children.length > 0) {
                        node.expanded = true;
                        node.hasChildren = true;
                        this.updateFlatList();
                    } else {
                        node.hasChildren = false;
                    }
                });
            } else {
                // Contraer todos los hijos para mostrar solo el nivel inmediato
                this.collapseDescendants(node);
                node.expanded = true;
                this.updateFlatList();
            }
        } else {
            node.expanded = false;
            this.updateFlatList();
        }
    }

    private collapseDescendants(node: ProjectTreeNode): void {
        if (!node.children) return;
        for (const child of node.children) {
            child.expanded = false;
            this.collapseDescendants(child);
        }
    }

    /**
     * Carga los hijos de un nodo desde el backend
     * @param node El nodo del cual cargar los hijos
     * @param callback Función a ejecutar después de cargar los hijos
     */
    private loadChildrenForNode(node: ProjectTreeNode, callback?: () => void): void {
        if (!node.id) {
            return;
        }

        node.isLoading = true;

        // Buscar proyectos cuyo parent_id sea el id del nodo actual
        const searchObj = { parentID: node.id };
        const searchString = JSON.stringify(searchObj);

        this.projectsService.list(searchString, 0, 10000, 'id ASC', false).pipe(takeUntil(this.destroying$)).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }

                // Filtrar SOLO los hijos directos del nodo actual
                const childrenProjects = projects.filter((project: any) => {
                    const projectParentId = project.parent_id?.toString() || 
                                          project.ParentID?.toString() || 
                                          project.parentId?.toString() || 
                                          project.ParentId?.toString();
                    return projectParentId === node.id;
                });

                // Verificar cuáles de estos hijos tienen hijos propios antes de mapearlos
                this.verifyNodesWithChildren(childrenProjects).then(() => {
                // Mapear los proyectos a ProjectTreeNode
                const children = childrenProjects.map((project: any) => {
                    const frontendData = mapBackendToFrontend(project);
                        const hasChildrenValue = (project as any).hasChildren;
                    return {
                        id: frontendData.id || project.id?.toString() || '',
                        name: frontendData.proyecto || project.name || '',
                        code: frontendData.codigo || project.code || '',
                        description: `${frontendData.tipo || ''} - ${frontendData.origen || ''}`,
                        level: frontendData.level || project.level || 0,
                        parentId: frontendData.parentId || (project.parent_id ? project.parent_id.toString() : undefined),
                        expanded: false,
                            hasChildren: Boolean(hasChildrenValue),
                        children: [],
                        isLoading: false,
                        createdAt: project.created_at ? new Date(project.created_at) : new Date(),
                        updatedAt: project.updated_at ? new Date(project.updated_at) : undefined,
                        active: frontendData.activo !== false,
                        visible: frontendData.visible !== false,
                        aprobado: frontendData.aprobado || false,
                        requiereObservacion: frontendData.requiereObservacion || false,
                        horasExtras: frontendData.horasExtras !== false,
                        horasNormales: frontendData.horasNormales !== false,
                        moneda: frontendData.moneda,
                        pep: frontendData.pep,
                        fechaInicio: frontendData.fechaInicio,
                        fechaTermino: frontendData.fechaTermino,
                        fechaEstimadaTermino: frontendData.fechaEstimadaTermino,
                        id_jefeProyecto: frontendData.id_jefeProyecto,
                        id_controlProyecto: frontendData.id_controlProyecto,
                        id_controlDocumental: frontendData.id_controlDocumental,
                        HHEstimadas: frontendData.HHEstimadas,
                        tipo: frontendData.tipo || project.tipo || project.Tipo || '',
                        id_cliente: frontendData.id_cliente || project.id_cliente || project.IdCliente || '',
                            origen: frontendData.origen || project.origen || project.Origen || '',
                            codigoPadre: frontendData.codigoPadre || project.codigo_padre || project.CodigoPadre || undefined,
                            codigoGlosa: frontendData.codigoGlosa || project.codigo_glosa || project.CodigoGlosa || undefined,
                            codigoLista: frontendData.codigoLista || project.codigo_lista || project.CodigoLista || undefined
                    } as ProjectTreeNode;
                });

                // Asignar los hijos al nodo
                node.children = children;
                node.hasChildren = children.length > 0;
                node.isLoading = false;
                
                console.log(`Hijos cargados para nodo ${node.id}:`, children.length);
                    console.log('Nodos hijos con hasChildren:', children.filter(c => c.hasChildren).map(c => `${c.id} (${c.name}): hasChildren=${c.hasChildren}`));

                    // Ejecutar callback si existe (esto expandirá el nodo y actualizará la lista)
                    if (callback) {
                        callback();
                    } else {
                        // Actualizar la lista plana
                        this.updateFlatList();
                    }
                });

                // Ejecutar callback si existe (esto expandirá el nodo y actualizará la lista)
                if (callback) {
                    callback();
                } else {
                    // Actualizar la lista plana
                    this.updateFlatList();
                }
            },
            error: (err) => {
                console.error('Error al cargar hijos del nodo:', node.id, err);
                node.isLoading = false;
                node.hasChildren = false;
                node.children = [];
                this.updateFlatList();
            }
        });
    }

    /**
     * Calcula la indentación basándose en la profundidad del nodo en el árbol
     * @param node El nodo del cual calcular la indentación
     * @returns El string de padding-left en píxeles
     */
    getIndentation(node: ProjectTreeNode): string {
        // Calcular la profundidad del nodo en el árbol
        const depth = this.getNodeDepth(node);
        return `${depth * 20}px`;
    }
    
    /**
     * Calcula la profundidad de un nodo en el árbol basándose en su parent_id
     * @param node El nodo del cual calcular la profundidad
     * @returns La profundidad del nodo (0 = raíz, 1 = hijo, 2 = nieto, etc.)
     */
    private getNodeDepth(node: ProjectTreeNode): number {
        if (!node.parentId) {
            return 0; // Es un nodo raíz
        }
        
        // Buscar el nodo padre en el árbol
        const parentNode = this.findNodeById(this.nodeList, node.parentId);
        if (parentNode) {
            // La profundidad es la profundidad del padre + 1
            return this.getNodeDepth(parentNode) + 1;
        }
        
        // Si no se encuentra el padre, asumir que es nivel 0
        return 0;
    }
    
    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        const baseColumns = this.columns.filter(column => column.visible).map(column => column.property);
        return ['name', 'code', 'active', 'visible', 'requiereObservacion', 'selectedReqObs', ...baseColumns.filter(c => c === 'actions')];
    }

    /**
     * Construye el objeto de búsqueda para el backend
     */
    private getSearch(): any {
        const searchValue = this.searchForm.get('search')?.value;
        const tipoFiltro = this.searchForm.get('tipo')?.value;
        
        const searchObj: any = {};
        
        // Si hay texto de búsqueda, buscar por nombre y código
        if (searchValue && searchValue.trim() !== '') {
            // El backend busca con LIKE, así que agregamos % al inicio y final
            searchObj.name = '%' + searchValue.toString().toUpperCase() + '%';
        }
        
        // El filtro de tipo se aplica en el frontend después de cargar
        // porque el backend no tiene ese campo en SearchProject
        
        return searchObj;
    }

    buscar() {
        // Al buscar, volver a la primera página
        this.currentPage = 0;
        this.loadData();
    }

    /**
     * Maneja el cambio de página
     */
    pageChanged(event: PageEvent) {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        
        const tipoFiltro = this.searchForm.get('tipo')?.value;
        const searchValue = this.searchForm.get('search')?.value;
        const hasSearch = searchValue && searchValue.trim() !== '';
        const needsFrontendPagination = (tipoFiltro && tipoFiltro !== '') || hasSearch;
        
        // Si hay filtro de tipo o búsqueda, usar paginación del frontend
        if (needsFrontendPagination) {
            // Aplicar paginación del frontend sobre la lista filtrada
            const startIndex = this.currentPage * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            this.flatNodeList = this.flatNodeListFiltered.slice(startIndex, endIndex);
            this.dataSource.data = this.flatNodeList;
            
            // Actualizar el paginator
            if (this.paginator) {
                this.paginator.length = this.totalRecords;
                this.paginator.pageIndex = this.currentPage;
                this.paginator.pageSize = this.pageSize;
            }
        } else {
            // Si no hay filtros, recargar desde el backend
            this.loadData();
        }
    }

    private filterNodes(searchTerm: string) {
        // Filtrar nodos que coincidan con el término de búsqueda
        const filteredNodes = this.searchInNodes(this.nodeList, searchTerm);
        this.flatNodeList = [];
        this.flattenTree(filteredNodes);
        this.dataSource.data = this.flatNodeList;
    }

    private searchInNodes(nodes: ProjectTreeNode[], searchTerm: string): ProjectTreeNode[] {
        const result: ProjectTreeNode[] = [];
        
        for (const node of nodes) {
            const nodeMatches = node.name.toLowerCase().includes(searchTerm) ||
                               node.code?.toLowerCase().includes(searchTerm) ||
                               node.description?.toLowerCase().includes(searchTerm);

            if (nodeMatches) {
                const clonedNode = { ...node, expanded: true };
                result.push(clonedNode);
            } else if (node.children && node.children.length > 0) {
                const matchingChildren = this.searchInNodes(node.children, searchTerm);
                if (matchingChildren.length > 0) {
                    const clonedNode = { ...node, children: matchingChildren, expanded: true };
                    result.push(clonedNode);
                }
            }
        }
        
        return result;
    }

    createRootNode() {
        this.dialog.open(ProjectNodeCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '60%',
            panelClass: 'full-screen-modal',
            data: { 
                parentId: null, 
                parentNode: undefined, // No hay padre para nodos raíz
                level: 0,
                allNodes: this.flatNodeList // Pasar todos los nodos para consistencia
            }
        }).afterClosed().subscribe((result: ProjectFormData) => {
            if (result) {
                // Recargar datos del backend para mantener sincronización
                this.loadData();
            }
        });
    }

    createChildNode(parentNode: ProjectTreeNode) {
        this.dialog.open(ProjectNodeCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '60%',
            panelClass: 'full-screen-modal',
            data: { 
                parentId: parentNode.id, 
                parentNode: parentNode, // Pasar el nodo padre completo
                level: parentNode.level + 1,
                allNodes: this.flatNodeList // Pasar todos los nodos para contar hermanos
            }
        }).afterClosed().subscribe((result: ProjectFormData) => {
            if (result) {
                // Recargar datos del backend para mantener sincronización
                this.loadData();
            }
        });
    }

    updateNode(node: ProjectTreeNode) {
        // Buscar el nodo padre si existe
        let parentNode: ProjectTreeNode | undefined;
        if (node.parentId) {
            parentNode = this.findNodeById(this.nodeList, node.parentId);
            // Si no se encuentra en la lista principal, buscar en la lista plana
            if (!parentNode && this.flatNodeList) {
                parentNode = this.flatNodeList.find(n => n.id === node.parentId);
            }
        }

        this.dialog.open(ProjectNodeCreateUpdateComponent, {
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '70%',
            panelClass: 'full-screen-modal',
            data: { 
                node: node,
                parentId: node.parentId,
                parentNode: parentNode, // Pasar el nodo padre si existe
                level: node.level,
                allNodes: this.flatNodeList
            }
        }).afterClosed().subscribe((result: ProjectFormData) => {
            if (result) {
                // Si se quiere crear un hermano, abrir diálogo para crear hijo del mismo padre
                if ((result as any).createSibling && (result as any).parentNode) {
                    this.createChildNode((result as any).parentNode);
                } else if (result.proyecto) {
                    // Actualizar el nodo en el árbol con los datos devueltos
                    const updatedNode = this.findNodeById(this.nodeList, node.id);
                    if (updatedNode) {
                        this.updateNodeInTree(result, updatedNode);
                    } else {
                        // Si no se encuentra el nodo, recargar todos los datos
                    this.loadData();
                    }
                }
            }
        });
    }

    deleteNode(node: ProjectTreeNode) {
        if (confirm(`¿Está seguro de eliminar el nodo "${node.name}" y todos sus hijos?`)) {
            // TODO: Implementar eliminación en el backend
            // Por ahora, solo actualizar la vista local
            this.removeNodeFromTree(node);
            this.handleSuccessResponse('Nodo eliminado correctamente');
            this.updateFlatList();
            // Cuando se implemente en el backend, recargar datos:
            // this.loadData();
        }
    }

    private addNodeToTree(nodeData: ProjectFormData, parentNode?: ProjectTreeNode) {
        const newNode: ProjectTreeNode = {
            id: this.generateId(),
            name: nodeData.proyecto,
            code: nodeData.codigo,
            description: `${nodeData.tipo} - ${nodeData.origen}`,
            level: parentNode ? parentNode.level + 1 : 0,
            parentId: nodeData.parentId,
            expanded: false,
            hasChildren: false,
            children: [],
            createdAt: new Date(),
            active: nodeData.activo,
            visible: nodeData.visible !== false,
            aprobado: nodeData.aprobado,
            requiereObservacion: nodeData.requiereObservacion,
            horasExtras: nodeData.horasExtras,
            horasNormales: nodeData.horasNormales,
            moneda: nodeData.moneda,
            pep: nodeData.pep,
            fechaInicio: nodeData.fechaInicio,
            fechaTermino: nodeData.fechaTermino,
            fechaEstimadaTermino: nodeData.fechaEstimadaTermino,
            id_jefeProyecto: nodeData.id_jefeProyecto,
            id_controlProyecto: nodeData.id_controlProyecto,
            id_controlDocumental: nodeData.id_controlDocumental,
            HHEstimadas: nodeData.HHEstimadas
        };

        if (parentNode) {
            if (!parentNode.children) {
                parentNode.children = [];
            }
            parentNode.children.push(newNode);
            parentNode.hasChildren = true;
            parentNode.expanded = true; // Expandir para mostrar el nuevo hijo
        } else {
            this.nodeList.push(newNode);
        }

        this.updateFlatList();
        this.handleSuccessResponse('Nodo creado correctamente');
    }

    private updateNodeInTree(nodeData: ProjectFormData, targetNode: ProjectTreeNode) {
        const oldParentId = targetNode.parentId;
        const newParentId = nodeData.parentId || undefined;
        const parentChanged = oldParentId !== newParentId;

        targetNode.name = nodeData.proyecto;
        targetNode.code = nodeData.codigo;
        targetNode.description = `${nodeData.tipo} - ${nodeData.origen}`;
        targetNode.updatedAt = new Date();
        targetNode.active = nodeData.activo;
        targetNode.visible = nodeData.visible !== false;
        targetNode.aprobado = nodeData.aprobado;
        targetNode.requiereObservacion = nodeData.requiereObservacion;
        targetNode.horasExtras = nodeData.horasExtras;
        targetNode.horasNormales = nodeData.horasNormales;
        targetNode.moneda = nodeData.moneda;
        targetNode.pep = nodeData.pep;
        targetNode.fechaInicio = nodeData.fechaInicio;
        targetNode.fechaTermino = nodeData.fechaTermino;
        targetNode.fechaEstimadaTermino = nodeData.fechaEstimadaTermino;
        targetNode.id_jefeProyecto = nodeData.id_jefeProyecto;
        targetNode.id_controlProyecto = nodeData.id_controlProyecto;
        targetNode.id_controlDocumental = nodeData.id_controlDocumental;
        targetNode.HHEstimadas = nodeData.HHEstimadas;
        targetNode.parentId = newParentId;
        targetNode.level = newParentId ? (this.findNodeById(this.nodeList, newParentId)?.level ?? targetNode.level) + 1 : 0;
        // Actualizar los campos separados del código
        targetNode.codigoPadre = nodeData.codigoPadre;
        targetNode.codigoGlosa = nodeData.codigoGlosa;
        targetNode.codigoLista = nodeData.codigoLista;

        if (parentChanged) {
            // Quitar el nodo de su padre actual
            if (oldParentId) {
                const oldParent = this.findNodeById(this.nodeList, oldParentId);
                if (oldParent?.children) {
                    oldParent.children = oldParent.children.filter(c => c.id !== targetNode.id);
                    oldParent.hasChildren = (oldParent.children?.length ?? 0) > 0;
                }
            } else {
                this.nodeList = this.nodeList.filter(n => n.id !== targetNode.id);
            }
            // Colocar el nodo en su nueva posición
            if (newParentId) {
                const newParent = this.findNodeById(this.nodeList, newParentId);
                if (newParent) {
                    if (!newParent.children) newParent.children = [];
                    newParent.children.push(targetNode);
                    newParent.hasChildren = true;
                    newParent.expanded = true;
                } else {
                    this.nodeList.push(targetNode);
                }
            } else {
                this.nodeList.push(targetNode);
            }
        }

        this.updateFlatList();
        this.handleSuccessResponse('Nodo actualizado correctamente');
    }

    private removeNodeFromTree(nodeToRemove: ProjectTreeNode) {
        if (nodeToRemove.parentId) {
            // Buscar el nodo padre y remover el hijo
            const parentNode = this.findNodeById(this.nodeList, nodeToRemove.parentId);
            if (parentNode && parentNode.children) {
                parentNode.children = parentNode.children.filter(child => child.id !== nodeToRemove.id);
                parentNode.hasChildren = parentNode.children.length > 0;
            }
        } else {
            // Remover del nivel raíz
            this.nodeList = this.nodeList.filter(node => node.id !== nodeToRemove.id);
        }
    }

    private findNodeById(nodes: ProjectTreeNode[], id: string): ProjectTreeNode | null {
        for (const node of nodes) {
            if (node.id === id) {
                return node;
            }
            if (node.children) {
                const found = this.findNodeById(node.children, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    private generateId(): string {
        return 'node-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    }

    handleSuccessResponse(message: string) {
        this.snackbar.open(message, '', {
            duration: 3000
        });
    }

    expandOneLevel() {
        const deepestExpanded = this.getDeepestExpandedLevel(this.nodeList, 0);
        this.expandNodesAtLevel(this.nodeList, 0, deepestExpanded);
        this.updateFlatList();
    }

    collapseOneLevel() {
        const deepestExpanded = this.getDeepestExpandedLevel(this.nodeList, 0);
        if (deepestExpanded < 0) return;
        this.collapseNodesAtLevel(this.nodeList, 0, deepestExpanded);
        this.updateFlatList();
    }

    private getDeepestExpandedLevel(nodes: ProjectTreeNode[], currentLevel: number): number {
        let deepest = -1;
        for (const node of nodes) {
            if (node.expanded && node.children && node.children.length > 0) {
                deepest = Math.max(deepest, currentLevel);
                const childDeepest = this.getDeepestExpandedLevel(node.children, currentLevel + 1);
                deepest = Math.max(deepest, childDeepest);
            }
        }
        return deepest;
    }

    private expandNodesAtLevel(nodes: ProjectTreeNode[], currentLevel: number, targetLevel: number): void {
        for (const node of nodes) {
            if (currentLevel === targetLevel + 1 || (targetLevel < 0 && currentLevel === 0)) {
                if (node.hasChildren || (node.children && node.children.length > 0)) {
                    if (!node.children || node.children.length === 0) {
                        this.loadChildrenForNode(node, () => {
                            if (node.children && node.children.length > 0) {
                                node.expanded = true;
                                node.hasChildren = true;
                                this.updateFlatList();
                            }
                        });
                    } else {
                        node.expanded = true;
                    }
                }
            } else if (node.expanded && node.children && node.children.length > 0) {
                this.expandNodesAtLevel(node.children, currentLevel + 1, targetLevel);
            }
        }
    }

    private collapseNodesAtLevel(nodes: ProjectTreeNode[], currentLevel: number, targetLevel: number): void {
        for (const node of nodes) {
            if (currentLevel === targetLevel && node.expanded) {
                node.expanded = false;
            } else if (node.expanded && node.children && node.children.length > 0) {
                this.collapseNodesAtLevel(node.children, currentLevel + 1, targetLevel);
            }
        }
    }

    /**
     * Exporta todos los proyectos a Excel con todos los campos y respetando la jerarquía
     */
    private allLevels: Level[] = [];
    private levelsById: Map<number, Level> = new Map();

    export() {
        this.isLoading = true;
        
        this.levelsService.list('', 0, 10000, 'id ASC').subscribe({
            next: (levelsResp: any) => {
                this.allLevels = levelsResp?.data || levelsResp || [];
                this.levelsById.clear();
                this.allLevels.forEach((lv: any) => {
                    const id = lv.id || lv.ID;
                    if (id) this.levelsById.set(id, lv);
                });
                this.exportProjects();
            },
            error: () => {
                this.allLevels = [];
                this.levelsById.clear();
                this.exportProjects();
            }
        });
    }

    private exportProjects() {
        const searchObj = this.getSearch();
        const searchString = Object.keys(searchObj).length > 0 ? JSON.stringify(searchObj) : '';
        
        this.projectsService.list(searchString, 0, 100000, 'id ASC', false).subscribe({
            next: (response: any) => {
                let projects: any[] = [];
                
                if (Array.isArray(response)) {
                    projects = response;
                } else if (response?.Data && Array.isArray(response.Data)) {
                    projects = response.Data;
                } else if (response?.data && Array.isArray(response.data)) {
                    projects = response.data;
                }
                
                // Crear un mapa de proyectos originales por ID para acceder a todos los campos
                const projectsMap = new Map<string, any>();
                projects.forEach((project: any) => {
                    const id = project.id?.toString() || project.ID?.toString() || '';
                    if (id) {
                        projectsMap.set(id, project);
                    }
                });
                
                // Convertir datos del backend a estructura de árbol
                const fullNodeList = this.buildTreeFromBackendData(projects);
                
                // Aplicar filtro por tipo si está seleccionado
                const tipoFiltro = this.searchForm.get('tipo')?.value;
                let filteredNodeList = fullNodeList;
                if (tipoFiltro && tipoFiltro !== '') {
                    filteredNodeList = this.filterTreeByType(fullNodeList, tipoFiltro);
                }
                
                // Convertir árbol a lista plana para exportar
                const exportData: any[] = [];
                this.flattenTreeForExport(filteredNodeList, exportData, 0, projectsMap);
                
                // Exportar a Excel
                TableUtil.exportArrayToExcel(exportData, "Proyectos");
                
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error al exportar proyectos:', error);
                this.handleErrorResponse('Error al exportar los proyectos');
                this.isLoading = false;
            }
        });
    }

    /**
     * Convierte el árbol de nodos a una lista plana para exportar, respetando la jerarquía
     */
    /**
     * Busca el Level correcto para un codigoLista, desambiguando por el parentLevelId
     * cuando hay codes duplicados entre niveles jerárquicos.
     */
    private getLevelCode(lv: any): string {
        return String(lv.code || lv.Code || '').trim();
    }

    private getLevelParentId(lv: any): number | null {
        const pid = lv.parent_id ?? lv.ParentID ?? lv.parentId;
        return (pid === null || pid === undefined || pid === 0) ? null : Number(pid);
    }

    private getLevelId(lv: any): number | undefined {
        const id = lv.id ?? lv.ID;
        return id !== null && id !== undefined ? Number(id) : undefined;
    }

    private findLevel(codigoLista: string, parentLevelId?: number): Level | undefined {
        if (!codigoLista) return undefined;
        const code = String(codigoLista).trim();

        if (parentLevelId) {
            const child = this.allLevels.find(lv =>
                this.getLevelCode(lv) === code &&
                this.getLevelParentId(lv) === parentLevelId
            );
            if (child) return child;
        }

        const topLevel = this.allLevels.find(lv =>
            this.getLevelCode(lv) === code && this.getLevelParentId(lv) === null
        );
        if (topLevel) return topLevel;

        return this.allLevels.find(lv => this.getLevelCode(lv) === code);
    }

    private flattenTreeForExport(
        nodes: ProjectTreeNode[], result: any[], level: number,
        projectsMap: Map<string, any>,
        inheritedPep?: string,
        inheritedSubtipo1?: string,
        parentLevelId?: number,
        inheritedTipo?: string
    ): void {
        nodes.forEach(node => {
            const originalProject = projectsMap.get(node.id) || {};
            const frontendData = mapBackendToFrontend(originalProject);
            
            const indentPrefix = '  '.repeat(Math.min(level, 10));

            const ownTipo = String(frontendData.tipo || originalProject.tipo || originalProject.Tipo || '');
            const tipoExport =
                inheritedTipo !== undefined && inheritedTipo !== '' ? inheritedTipo : ownTipo;
            
            const codigoLista = frontendData.codigoLista || originalProject.codigo_lista || originalProject.CodigoLista || '';
            const levelInfo = this.findLevel(codigoLista, parentLevelId);

            const subtipo1Propio = codigoLista
                ? `${codigoLista}${levelInfo ? ' - ' + levelInfo.description : ''}`
                : '';

            let subtipo1 = '';
            let subtipo2 = '';
            let idSubtipo: number | string = '';

            const levelId = levelInfo ? this.getLevelId(levelInfo) : undefined;

            if (codigoLista && inheritedSubtipo1) {
                subtipo1 = inheritedSubtipo1;
                subtipo2 = subtipo1Propio;
                idSubtipo = levelId || '';
            } else if (codigoLista) {
                subtipo1 = subtipo1Propio;
                idSubtipo = levelId || '';
            } else if (inheritedSubtipo1) {
                subtipo1 = inheritedSubtipo1;
            }

            const usesPep = this.tipoUsesPep(tipoExport);

            let nodePep = '';
            if (usesPep) {
                if (node.pep && inheritedPep) {
                    const pepIsLevelCode = this.allLevels.some(lv =>
                        String(lv.code || (lv as any).Code || '').trim() === String(node.pep).trim()
                    );
                    nodePep = pepIsLevelCode ? inheritedPep : node.pep;
                } else {
                    nodePep = node.pep || inheritedPep || '';
                }
            }

            const pepForChildren = usesPep ? nodePep : undefined;
            const subtipo1ParaHijos = subtipo1Propio || inheritedSubtipo1 || '';
            const levelIdParaHijos = levelId || parentLevelId;

            const exportRow: any = {
                'ID': node.id || '',
                'Nivel': level,
                'Proyecto': indentPrefix + (node.name || ''),
                'Código': node.code || '',
                'SubTipo1': subtipo1 || null,
                'Subtipo2': subtipo2 || null,
                'IdSubtipo': idSubtipo || null,
                'Activo': node.active ? 'Sí' : 'No',
                'Visible': node.visible !== false ? 'Sí' : 'No',
                'Aprobado': node.aprobado ? 'Sí' : 'No',
                'Requiere Observación': node.requiereObservacion ? 'Sí' : 'No',
                'Horas Normales': node.horasNormales !== false ? 'Sí' : 'No',
                'Horas Extras': node.horasExtras ? 'Sí' : 'No',
                'PEP': nodePep,
                'Moneda': node.moneda || '',
                'Tipo': tipoExport,
                'Cliente': frontendData.id_cliente || originalProject.id_cliente || originalProject.IdCliente || '',
                'Origen': frontendData.origen || originalProject.origen || originalProject.Origen || '',
                'Fecha Inicio': node.fechaInicio || '',
                'Fecha Término': node.fechaTermino || '',
                'Fecha Estimada Término': node.fechaEstimadaTermino || '',
                'Jefe de Proyecto': node.id_jefeProyecto || '',
                'Control de Proyecto': node.id_controlProyecto || '',
                'Control Documental': node.id_controlDocumental || '',
                'HH Estimadas': node.HHEstimadas || '',
                'ID Padre': node.parentId || '',
                'Fecha Creación': node.createdAt ? new Date(node.createdAt).toLocaleDateString() : '',
                'Fecha Actualización': node.updatedAt ? new Date(node.updatedAt).toLocaleDateString() : ''
            };
            
            result.push(exportRow);
            
            if (node.children && node.children.length > 0) {
                this.flattenTreeForExport(
                    node.children, result, level + 1, projectsMap,
                    pepForChildren, subtipo1ParaHijos, levelIdParaHijos,
                    tipoExport
                );
            }
        });
    }

    /**
     * Verifica si todos los elementos están seleccionados
     */
    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource?.data.length || 0;
        return numSelected === numRows && numRows > 0;
    }

    /**
     * Alterna la selección de todos los elementos
     */
    masterToggle(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.dataSource?.data.forEach(row => this.selection.select(row));
        }
    }

    /**
     * Verifica si todos los proyectos que requieren observación están seleccionados
     */
    isAllReqObsSelected(): boolean {
        const reqObsRows = this.dataSource?.data.filter(row => row.requiereObservacion) || [];
        if (reqObsRows.length === 0) return false;
        return reqObsRows.every(row => this.selection.isSelected(row));
    }

    /**
     * Verifica si algunos proyectos que requieren observación están seleccionados
     */
    isSomeReqObsSelected(): boolean {
        const reqObsRows = this.dataSource?.data.filter(row => row.requiereObservacion) || [];
        return reqObsRows.some(row => this.selection.isSelected(row));
    }

    /**
     * Alterna la selección de todos los proyectos que requieren observación
     */
    masterToggleReqObs(): void {
        const reqObsRows = this.dataSource?.data.filter(row => row.requiereObservacion) || [];
        if (this.isAllReqObsSelected()) {
            // Deseleccionar todos los que requieren observación
            reqObsRows.forEach(row => this.selection.deselect(row));
        } else {
            // Seleccionar todos los que requieren observación
            reqObsRows.forEach(row => this.selection.select(row));
        }
    }

    /**
     * Rechaza el forzado de comentarios en los proyectos seleccionados
     */
    async rechazarSelected(): Promise<void> {
        if (this.selection.selected.length === 0) {
            this.snackbar.open('Por favor seleccione al menos un proyecto', 'Cerrar', {
                duration: 3000
            });
            return;
        }

        const data = {
            title: "Rechazar forzado de comentarios",
            message: "¿Está seguro de rechazar el forzado de comentarios en los proyectos seleccionados?"
        };
        
        await this.changeForceSelected(data, false);
        this.loadData();
    }

    /**
     * Fuerza los comentarios en los proyectos seleccionados
     */
    async aprobarSelected(): Promise<void> {
        if (this.selection.selected.length === 0) {
            this.snackbar.open('Por favor seleccione al menos un proyecto', 'Cerrar', {
                duration: 3000
            });
            return;
        }

        const data = {
            title: "Forzar comentarios",
            message: "¿Está seguro de forzar los comentarios en los proyectos seleccionados?"
        };
        
        await this.changeForceSelected(data, true);
        this.loadData();
    }

    /**
     * Cambia el estado de force_comments en los proyectos seleccionados
     */
    async changeForceSelected(data: any, forceComments: boolean): Promise<void> {
        const dialogRef = this.dialog.open(DialogComponent, {
            data: data,
            disableClose: true,
            width: '400px'
        });

        const result = await dialogRef.afterClosed().toPromise();

        if (!result) {
            this.selection.clear();
            return;
        }

        return new Promise((resolve) => {
            const projects: any[] = [];
            
            // Función auxiliar para buscar un nodo en el árbol por ID
            const findNodeInTree = (nodes: ProjectTreeNode[], id: string): ProjectTreeNode | null => {
                for (const node of nodes) {
                    if (node.id === id) {
                        return node;
                    }
                    if (node.children && node.children.length > 0) {
                        const found = findNodeInTree(node.children, id);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            this.selection.selected.forEach((node) => {
                // Buscar el nodo completo en el árbol para obtener todos los datos
                const fullNode = findNodeInTree(this.nodeList, node.id) || node;
                
                const projectData: ProjectFormData = {
                    id: fullNode.id,
                    proyecto: fullNode.name || '',
                    codigo: fullNode.code || '',
                    activo: fullNode.active !== false,
                    visible: fullNode.visible !== false,
                    requiereObservacion: forceComments,
                    horasExtras: fullNode.horasExtras !== false,
                    horasNormales: fullNode.horasNormales !== false,
                    tipo: (fullNode as any).tipo || '',
                    id_cliente: (fullNode as any).id_cliente || '',
                    origen: (fullNode as any).origen || '',
                    moneda: fullNode.moneda,
                    pep: fullNode.pep,
                    aprobado: fullNode.aprobado || false,
                    fechaInicio: fullNode.fechaInicio,
                    fechaTermino: fullNode.fechaTermino,
                    fechaEstimadaTermino: fullNode.fechaEstimadaTermino,
                    id_jefeProyecto: fullNode.id_jefeProyecto,
                    id_controlProyecto: fullNode.id_controlProyecto,
                    id_controlDocumental: fullNode.id_controlDocumental,
                    HHEstimadas: fullNode.HHEstimadas,
                    parentId: fullNode.parentId,
                    level: fullNode.level
                };

                const backendData = mapFrontendToBackend(projectData);
                projects.push(backendData);
            });

            this.projectsService.update(projects).subscribe({
                next: (result) => {
                    console.log('Proyectos actualizados:', result);
                },
                error: (err) => {
                    console.error('Error al actualizar proyectos:', err);
                    this.handleErrorResponse('Error al actualizar los proyectos');
                },
                complete: () => {
                    this.selection.clear();
                    this.snackbar.open(
                        `Se ${forceComments ? 'forzaron' : 'rechazaron'} los comentarios en ${projects.length} proyecto(s)`,
                        'Cerrar',
                        { duration: 3000 }
                    );
                    resolve();
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.destroying$.next();
        this.destroying$.complete();
    }
}
